import React, { useState, useEffect } from 'react';
import ClariMeLogo from './ClariMeLogo';

// ── Inline Icons ────────────────────────────────────────────────────

const ShieldCheckIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

const UserCheckIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11l2 2 4-4" />
  </svg>
);

const ScaleIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
  </svg>
);

const LockIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);

const XCircleIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const CheckIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const ArrowLeftIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
  </svg>
);

const DocumentIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const HeartPulseIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12h4l3-6 4 12 3-6h4" />
  </svg>
);


// ── Provider Info Page ──────────────────────────────────────────────

export default function ProviderInfoPage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">

      {/* ── Nav ──────────────────────────────────────────────── */}
      <nav
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/90 backdrop-blur-xl shadow-sm shadow-slate-200/60 border-b border-slate-100'
            : 'bg-white border-b border-slate-100'
        }`}
      >
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="#/" className="flex items-center gap-3 text-slate-400 hover:text-slate-600 transition-colors">
            <ArrowLeftIcon className="w-4 h-4" />
            <ClariMeLogo size="small" variant="light" />
          </a>
          <a
            href="mailto:provider@clarime.health"
            className="text-sm text-teal-600 hover:text-teal-700 font-medium transition-colors"
          >
            Contact Us
          </a>
        </div>
      </nav>

      {/* ── Header ───────────────────────────────────────────── */}
      <section className="bg-slate-50 border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-6 py-10 sm:py-14">
          <p className="text-sm font-semibold text-teal-600 uppercase tracking-wider mb-2">For Healthcare Providers</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight leading-tight mb-4">
            ClariMe Provider Information
          </h1>
        </div>
      </section>

      {/* ── Mission ──────────────────────────────────────────── */}
      <section className="border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-6 py-10 sm:py-14">
          <div className="bg-gradient-to-br from-teal-50/80 to-cyan-50/40 rounded-xl border border-teal-100 p-6 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-3">Our Mission</h2>
            <p className="text-slate-700 leading-relaxed mb-4">
              Patients forget 40 to 80% of what they're told during a medical visit. When they leave the exam room, they're often unable to accurately relay critical information to the family members who help them manage their care. Medications, follow-up plans, and next steps get lost or confused.
            </p>
            <p className="text-slate-700 leading-relaxed mb-4">
              ClariMe exists to close that gap. We help patients take control of their own care by giving them a clear record of what was discussed and an easy way to involve their care team at home.
            </p>
            <p className="text-slate-700 leading-relaxed font-medium">
              Better-informed patients and caregivers lead to better adherence, fewer miscommunications, and better outcomes. That's what ClariMe is about.
            </p>
          </div>
        </div>
      </section>

      {/* ── Content ──────────────────────────────────────────── */}
      <div className="max-w-4xl mx-auto px-6 py-10 sm:py-14 space-y-12">

        {/* What ClariMe Does */}
        <div>
          <SectionHeader icon={<HeartPulseIcon className="w-5 h-5" />} title="What ClariMe Does" />
          <BulletList items={[
            'Patients record their visit on their own phone',
            'ClariMe generates a plain-language summary of the conversation including key points, medications, and follow-ups',
            'Patients can share the summary with family members they\'ve designated ("Care Groups")',
            'Designed to improve patient recall. Studies show patients forget 40 to 80% of what\'s discussed',
          ]} />
        </div>

        {/* What ClariMe Does NOT Do */}
        <div>
          <SectionHeader icon={<XCircleIcon className="w-5 h-5" />} title="What ClariMe Does Not Do" />
          <BulletList variant="negative" items={[
            'Does not generate clinical notes or interface with EHR systems',
            'Does not diagnose, treat, or make clinical recommendations',
            'Does not record without the patient first confirming provider awareness',
            'Does not share data with third parties, advertisers, or anyone the patient hasn\'t authorized',
          ]} />
        </div>

        {/* Consent */}
        <div>
          <SectionHeader icon={<UserCheckIcon className="w-5 h-5" />} title="Recording Consent" />
          <p className="text-slate-600 text-sm mb-4">
            Before every recording, the patient must confirm on-screen that:
          </p>
          <BulletList items={[
            'They have informed their provider the visit will be recorded',
            'The provider has acknowledged and does not object',
            'The recording is for personal use and care coordination only',
          ]} />
          <p className="text-slate-500 text-sm mt-4">
            The patient cannot bypass this step. If a provider prefers not to be recorded, the patient cannot proceed.
          </p>
        </div>

        {/* HIPAA */}
        <div>
          <SectionHeader icon={<ShieldCheckIcon className="w-5 h-5" />} title="HIPAA Compliance" />
          <BulletList items={[
            'All audio and summaries encrypted at rest (AES-256) and in transit (TLS 1.2+)',
            'Built entirely on AWS HIPAA-eligible services with signed Business Associate Agreements',
            'Medical-grade transcription via AWS Transcribe Medical',
            'Patient controls all access. Only they decide who sees their summary',
            'Audio and summaries can be permanently deleted by the patient at any time',
          ]} />
        </div>

        {/* Liability */}
        <div>
          <SectionHeader icon={<ScaleIcon className="w-5 h-5" />} title="Provider Liability" />
          <BulletList items={[
            'ClariMe is patient-initiated and patient-controlled. The provider\'s workflow does not change',
            'No change to your standard of care or documentation requirements',
            'Summaries are clearly labeled as patient aids, not medical records',
            'Providers do not create accounts, sign agreements, or access any ClariMe data',
            'ClariMe Health LLC maintains its own professional liability coverage',
          ]} />
          <p className="text-slate-500 text-sm mt-4">
            Providers with questions about recording in their practice context should consult their own risk management or legal counsel.
          </p>
        </div>

        {/* Benefits */}
        <div>
          <SectionHeader icon={<HeartPulseIcon className="w-5 h-5" />} title="How This Helps Your Patients" />
          <BulletList items={[
            'Better medication adherence because patients have written instructions to reference',
            'Fewer "what did the doctor say?" callbacks from family members',
            'Improved follow-up compliance with clear next steps patients can review at home',
            'More informed caregiver support for medication management and appointments',
          ]} />
        </div>

        {/* About / Contact */}
        <div className="border-t border-slate-200 pt-10">
          <div className="bg-slate-50 rounded-xl border border-slate-200 p-6">
            <p className="text-sm font-semibold text-slate-800 mb-2">About ClariMe Health</p>
            <p className="text-sm text-slate-600 leading-relaxed mb-4">
              ClariMe Health LLC is a healthcare technology company founded by a practicing physician. Our mission is to bridge the communication gap between the exam room and home. We're building ClariMe alongside the physicians who know patient care best. We welcome your feedback.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 text-sm">
              <div>
                <p className="text-slate-400 text-xs uppercase tracking-wider mb-0.5">Provider Inquiries</p>
                <a href="mailto:provider@clarime.health" className="text-teal-600 hover:text-teal-700 transition-colors">provider@clarime.health</a>
              </div>
              <div>
                <p className="text-slate-400 text-xs uppercase tracking-wider mb-0.5">Website</p>
                <a href="https://clarime.health" className="text-teal-600 hover:text-teal-700 transition-colors">clarime.health</a>
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* ── Footer ───────────────────────────────────────────── */}
      <footer className="border-t border-slate-100 py-8 px-6 bg-slate-50">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <ClariMeLogo size="small" variant="light" />
          <div className="flex items-center gap-6 text-xs text-slate-400">
            <span>&copy; {new Date().getFullYear()} ClariMe Health LLC</span>
            <a href="#/" className="hover:text-slate-600 transition-colors">Patient Site</a>
            <a href="#/providers" className="text-teal-500">Provider Info</a>
          </div>
        </div>
      </footer>
    </div>
  );
}


// ── Helper Components ───────────────────────────────────────────────

function SectionHeader({ icon, title }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className="w-9 h-9 bg-slate-100 rounded-lg flex items-center justify-center text-slate-500">
        {icon}
      </div>
      <h2 className="text-lg font-bold text-slate-900">{title}</h2>
    </div>
  );
}

function BulletList({ items, variant = 'positive' }) {
  return (
    <div className="space-y-2.5">
      {items.map((item, i) => (
        <div key={i} className="flex items-start gap-3">
          <div className={`mt-1 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
            variant === 'negative' ? 'bg-red-50' : 'bg-teal-50'
          }`}>
            {variant === 'negative' 
              ? <XCircleIcon className="w-3.5 h-3.5 text-red-400" />
              : <CheckIcon className="w-3 h-3 text-teal-600" />
            }
          </div>
          <p className="text-slate-600 text-sm leading-relaxed">{item}</p>
        </div>
      ))}
    </div>
  );
}
